import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { SystemModule } from '../../types/permissions';
import Layout from '../Layout/Layout';
import LoadingSpinner from '../Common/LoadingSpinner';

interface PermissionRouteProps {
  children: React.ReactNode;
  module: SystemModule;
  requireRead?: boolean;
}

/**
 * Componente de rota protegida que verifica autenticação E permissões específicas
 * Tenta recarregar permissões automaticamente antes de negar acesso
 *
 * @param children - Conteúdo a ser renderizado se o usuário tiver permissão
 * @param module - Módulo do sistema que requer permissão
 * @param requireRead - Se true, verifica permissão de leitura (padrão: true)
 */
const PermissionRoute: React.FC<PermissionRouteProps> = ({
  children,
  module,
  requireRead = true
}) => {
  const { isAuthenticated, isLoading, refreshPermissions } = useAuth();
  const { hasAccess, canRead, isAdmin } = usePermissions();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasTriedRefresh, setHasTriedRefresh] = useState(false);
  const location = useLocation();

  // Verificar se o usuário tem acesso ao módulo
  const hasModuleAccess = hasAccess(module);
  const hasReadPermission = requireRead ? canRead(module) : true;
  const hasPermission = hasModuleAccess && hasReadPermission;

  // Tentar recarregar permissões se não tiver acesso (uma única vez)
  useEffect(() => {
    const tryRefreshPermissions = async () => {
      if (
        isAuthenticated &&
        !isAdmin &&
        !hasPermission &&
        !hasTriedRefresh &&
        !isRefreshing
      ) {
        setIsRefreshing(true);
        setHasTriedRefresh(true);

        try {
          await refreshPermissions();
        } catch (error) {
          // Continuar mesmo com erro - será redirecionado para acesso negado
        } finally {
          setIsRefreshing(false);
        }
      }
    };

    tryRefreshPermissions();
  }, [
    isAuthenticated,
    isAdmin,
    hasPermission,
    hasTriedRefresh,
    isRefreshing,
    refreshPermissions
  ]);

  // Reset do estado de tentativa ao mudar de rota
  useEffect(() => {
    setHasTriedRefresh(false);
  }, [location.pathname]);

  // Mostrar loading enquanto verifica autenticação ou atualiza permissões
  if (isLoading || isRefreshing) {
    return <LoadingSpinner />;
  }

  // Se não autenticado, redirecionar para login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Admins têm acesso total a tudo
  if (isAdmin) {
    return <Layout>{children}</Layout>;
  }

  // Se não tem permissão E já tentou recarregar, redirecionar para acesso negado
  if (!hasPermission && hasTriedRefresh) {
    return <Navigate to="/acesso-negado" replace />;
  }

  // Se tem permissão, renderizar com Layout
  if (hasPermission) {
    return <Layout>{children}</Layout>;
  }

  // Estado intermediário (não deveria chegar aqui normalmente)
  return <LoadingSpinner />;
};

export default PermissionRoute;
